import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const DEV_MODE = String(process.env.REACT_APP_DEV_MODE || "false").toLowerCase() === "true";

// ------- Token storage -------
const ACCESS_KEY = "neopay-access-token";
const REFRESH_KEY = "neopay-refresh-token";

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access, refresh) {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

// ------- Axios instance with auth interceptor -------
export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = tokenStore.access;
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

let isRefreshing = false;
let queuedRequests = [];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retry &&
      tokenStore.refresh &&
      !original.url?.includes("/auth/refresh") &&
      !original.url?.includes("/auth/login")
    ) {
      original._retry = true;
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queuedRequests.push({ resolve, reject, original });
        });
      }
      isRefreshing = true;
      try {
        const { data } = await axios.post(`${API}/auth/refresh`, {
          refresh_token: tokenStore.refresh,
        });
        tokenStore.set(data.access_token, null);
        queuedRequests.forEach((q) => {
          q.original.headers.Authorization = `Bearer ${data.access_token}`;
          q.resolve(api(q.original));
        });
        queuedRequests = [];
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch (e) {
        queuedRequests.forEach((q) => q.reject(e));
        queuedRequests = [];
        tokenStore.clear();
        // Hard redirect to login
        if (typeof window !== "undefined") window.location.href = "/login";
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// ------- Public ledger endpoints (no auth) -------
const pub = axios.create({ baseURL: API });
export const ledgerApi = {
  stats: () => pub.get("/ledger/stats").then((r) => r.data),
  verify: () => pub.get("/ledger/verify").then((r) => r.data),
  listBlocks: (limit = 25, skip = 0) =>
    pub.get("/ledger/blocks", { params: { limit, skip } }).then((r) => r.data),
  getBlock: (n) => pub.get(`/ledger/blocks/${n}`).then((r) => r.data),
  getTx: (id) => pub.get(`/ledger/transactions/${id}`).then((r) => r.data),
  getWallet: (addr) => pub.get(`/ledger/wallets/${addr}`).then((r) => r.data),
  tamper: (block_number) =>
    pub
      .post("/ledger/_admin/tamper", null, {
        params: block_number != null && block_number !== "" ? { block_number } : {},
      })
      .then((r) => r.data),
  reset: () => pub.post("/ledger/_admin/reset").then((r) => r.data),
};

// ------- Auth -------
export const authApi = {
  signup: (body) => api.post("/auth/signup", body).then((r) => r.data),
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

// ------- Business -------
export const businessApi = {
  me: () => api.get("/business/me").then((r) => r.data),
  wallets: () => api.get("/business/wallets").then((r) => r.data),
  transactions: (params = {}) =>
    api.get("/business/transactions", { params }).then((r) => r.data),
  resolveVpa: (vpa) =>
    api.get("/business/vpa/resolve", { params: { vpa } }).then((r) => r.data),
  transfer: (body) => api.post("/business/transfer", body).then((r) => r.data),
  exportKey: (currency) =>
    api.get(`/business/wallets/${currency}/export`).then((r) => r.data),
};

// ------- Admin -------
export const adminApi = {
  getBranding: () => pub.get("/admin/branding").then((r) => r.data),
  updateBranding: (body) => api.put("/admin/branding", body).then((r) => r.data),
  listBusinesses: () => api.get("/admin/businesses").then((r) => r.data),
  fund: (body) => api.post("/admin/fund", body).then((r) => r.data),
  setBusinessType: (businessId, business_type) =>
    api.put(`/admin/businesses/${businessId}/business-type`, { business_type })
      .then((r) => r.data),
};

// ------- AI / Holds / Insights / Scheduled -------
export const aiApi = {
  parseNlp: (text) => api.post("/ai/nlp-payment", { text }).then((r) => r.data),
  confirmNlp: (draft_id, confirmation_text) =>
    api
      .post("/ai/nlp-payment/confirm", { draft_id, confirmation_text })
      .then((r) => r.data),
  myInsights: () => api.get("/business/insights").then((r) => r.data),
  refreshInsights: () => api.post("/business/insights/refresh").then((r) => r.data),
  myHolds: () => api.get("/business/holds").then((r) => r.data),
  approveHold: (id) => api.post(`/business/holds/${id}/approve`).then((r) => r.data),
  rejectHold: (id) => api.post(`/business/holds/${id}/reject`).then((r) => r.data),
  myScheduled: () => api.get("/scheduled-transfers").then((r) => r.data),
  cancelScheduled: (id) => api.delete(`/scheduled-transfers/${id}`).then((r) => r.data),
  // Super-admin
  allHolds: () => api.get("/admin/holds").then((r) => r.data),
  overrideHold: (id, approve) =>
    api.post(`/admin/holds/${id}/override`, null, { params: { approve } }).then((r) => r.data),
  listSanctions: () => api.get("/admin/compliance/sanctions").then((r) => r.data),
  addSanction: (body) => api.post("/admin/compliance/sanctions", body).then((r) => r.data),
  deleteSanction: (id) => api.delete(`/admin/compliance/sanctions/${id}`).then((r) => r.data),
  screen: (body) => api.post("/admin/compliance/screen", body).then((r) => r.data),
  riskDecisions: (params = {}) =>
    api.get("/admin/risk-decisions", { params }).then((r) => r.data),
  aiMetrics: () => api.get("/admin/ai/metrics").then((r) => r.data),
  getDegradation: () => api.get("/admin/ai/degradation").then((r) => r.data),
  setDegradation: (forced_offline) =>
    api.post("/admin/ai/degradation", { forced_offline }).then((r) => r.data),
  // DEV-only fraud force-decision
  getForceDecision: () =>
    api.get("/admin/fraud/force-decision").then((r) => r.data),
  setForceDecision: (mode, action_kind = null) =>
    api
      .post("/admin/fraud/force-decision", { mode, action_kind })
      .then((r) => r.data),
  status: () => api.get("/ai/status").then((r) => r.data),
  // Per-tx risk (auth-scoped, role-aware)
  txRisk: (tx_id) =>
    api.get(`/business/transactions/${tx_id}/risk`).then((r) => r.data),
};

// ------- Contracts (Phase 4) -------
export const contractsApi = {
  draft: (body) => api.post("/contracts/draft", body).then((r) => r.data),
  activate: (id, confirmation_text) =>
    api.post(`/contracts/${id}/activate`, { confirmation_text }).then((r) => r.data),
  cancel: (id) => api.post(`/contracts/${id}/cancel`).then((r) => r.data),
  confirm: (id) => api.post(`/contracts/${id}/confirm`).then((r) => r.data),
  dispute: (id) => api.post(`/contracts/${id}/dispute`).then((r) => r.data),
  resolve: (id, decision, note) =>
    api.post(`/contracts/${id}/resolve`, { decision, note }).then((r) => r.data),
  claim: (id) => api.post(`/contracts/${id}/claim`).then((r) => r.data),
  setFlag: (id, name, value) =>
    api.post(`/contracts/${id}/set-flag`, { name, value }).then((r) => r.data),
  list: (params = {}) => api.get("/contracts", { params }).then((r) => r.data),
  get: (id) => api.get(`/contracts/${id}`).then((r) => r.data),
  build: (description, contract_type) =>
    api.post("/ai/contract-builder", { description, contract_type }).then((r) => r.data),
  // Admin
  adminList: (params = {}) => api.get("/admin/contracts", { params }).then((r) => r.data),
  setOracle: (name, value) =>
    api.post(`/admin/oracle/${name}`, { value }).then((r) => r.data),
  listOracles: () => api.get("/admin/oracle").then((r) => r.data),
};

// ------- Invoices -------
export const invoicesApi = {
  create: (body) => api.post("/invoices", body).then((r) => r.data),
  list: (params = {}) => api.get("/invoices", { params }).then((r) => r.data),
  get: (id) => api.get(`/invoices/${id}`).then((r) => r.data),
  approve: (id) => api.post(`/invoices/${id}/approve`).then((r) => r.data),
  cancel: (id) => api.post(`/invoices/${id}/cancel`).then((r) => r.data),
  markPaid: (id) => api.post(`/invoices/${id}/mark-paid`).then((r) => r.data),
};

// ------- FX -------
export const fxApi = {
  rates: () => pub.get("/fx/rates").then((r) => r.data),
  quote: (body) => api.post("/fx/quote", body).then((r) => r.data),
  getQuote: (id) => api.get(`/fx/quote/${id}`).then((r) => r.data),
  adminRefresh: () => api.post("/admin/fx/refresh").then((r) => r.data),
  adminOverride: (currency, rate) =>
    api.post("/admin/fx/override", { currency, rate }).then((r) => r.data),
  adminClearOverride: (currency) =>
    api.delete(`/admin/fx/override/${currency}`).then((r) => r.data),
  adminAudit: () => api.get("/admin/fx/audit").then((r) => r.data),
  adminTreasury: () => api.get("/admin/fx/treasury").then((r) => r.data),
  adminConfig: (body) => api.put("/admin/fx/config", body).then((r) => r.data),
  adminResetDemo: () => api.post("/admin/fx/_reset_demo").then((r) => r.data),
};

// ------- Agents (Phase 5) -------
export const agentsApi = {
  list: () => api.get("/agents").then((r) => r.data),
  get: (id) => api.get(`/agents/${id}`).then((r) => r.data),
  create: (body) => api.post("/agents", body).then((r) => r.data),
  update: (id, body) => api.patch(`/agents/${id}`, body).then((r) => r.data),
  pause: (id) => api.post(`/agents/${id}/pause`).then((r) => r.data),
  resume: (id) => api.post(`/agents/${id}/resume`).then((r) => r.data),
  revoke: (id) => api.post(`/agents/${id}/revoke`).then((r) => r.data),
  listKeys: (id) => api.get(`/agents/${id}/keys`).then((r) => r.data),
  mintKey: (id) => api.post(`/agents/${id}/keys`).then((r) => r.data),
  revokeKey: (id, kid) => api.delete(`/agents/${id}/keys/${kid}`).then((r) => r.data),
  approvals: () => api.get("/business/agent-approvals").then((r) => r.data),
  approveIntent: (id) =>
    api.post(`/business/agent-approvals/${id}/approve`).then((r) => r.data),
  rejectIntent: (id) =>
    api.post(`/business/agent-approvals/${id}/reject`).then((r) => r.data),
  recentPayments: (limit = 25) =>
    api.get("/business/agent-payments", { params: { limit } }).then((r) => r.data),
  breaches: (id, limit = 100) =>
    api.get(`/agents/${id}/breaches`, { params: { limit } }).then((r) => r.data),
  incidents: (id, limit = 100) =>
    api.get(`/agents/${id}/incidents`, { params: { limit } }).then((r) => r.data),
  // demo
  runScenario: (agentId) =>
    api.post("/demo/run-agent-scenario", null, agentId ? { params: { agent_id: agentId } } : {}).then((r) => r.data),
  startLiveScenario: (agentId) =>
    api.post("/demo/scenario/start", null, agentId ? { params: { agent_id: agentId } } : {}).then((r) => r.data),
  getLiveScenario: (id) => api.get(`/demo/scenario/${id}`).then((r) => r.data),
  // Public verification (no auth)
  verifyReceipt: (receiptId) =>
    pub.get(`/x402/verify-receipt/${receiptId}`).then((r) => r.data),
  networkPubkey: () => pub.get("/x402/network-pubkey").then((r) => r.data),
};

// ------- Phase 6 — Validators + Proofs -------
export const validatorsApi = {
  list: () => pub.get("/validators").then((r) => r.data),
  get: (id) => pub.get(`/validators/${id}`).then((r) => r.data),
  blocks: (id, limit = 50) =>
    pub.get(`/validators/${id}/blocks`, { params: { limit } }).then((r) => r.data),
  setStatus: (id, status) =>
    api.post(`/admin/validators/${id}/set-status`, { status }).then((r) => r.data),
  setWeight: (id, weight) =>
    api.post(`/admin/validators/${id}/set-weight`, { weight }).then((r) => r.data),
  adminMetrics: () => api.get("/admin/validators/metrics").then((r) => r.data),
  consensusMetrics: () => api.get("/admin/consensus/metrics").then((r) => r.data),
};

// ------- Phase 6 polish — Embed Agent Pay + public agent profile -------
export const embedApi = {
  // private (auth required)
  mint: (agentId, body) =>
    api.post(`/agents/${agentId}/embed-tokens`, body).then((r) => r.data),
  list: (agentId) =>
    api.get(`/agents/${agentId}/embed-tokens`).then((r) => r.data),
  revoke: (agentId, tokenId) =>
    api.delete(`/agents/${agentId}/embed-tokens/${tokenId}`).then((r) => r.data),
  // public
  resolve: (token) => pub.get(`/x402/embed/${token}`).then((r) => r.data),
  compatibleAgents: (token) =>
    api.get(`/x402/embed/${token}/compatible-agents`).then((r) => r.data),
};

export const publicAgentApi = {
  get: (did) => pub.get(`/agents/public/${did}`).then((r) => r.data),
};

export const proofApi = {
  get: (txId) => pub.get(`/proof/${txId}`).then((r) => r.data),
  downloadUrl: (txId) => `${BACKEND_URL}/api/proof/${txId}/download`,
  verify: (proof) => pub.post("/proof/verify", { proof }).then((r) => r.data),
  spec: () => pub.get("/proof/spec").then((r) => r.data),
  specMarkdownUrl: () => `${BACKEND_URL}/api/proof/spec/markdown`,
};

// ------- Webhooks (Phase 8) -------
export const webhooksApi = {
  spec: () => pub.get("/webhooks/spec").then((r) => r.data),
  specMarkdown: () => pub.get("/webhooks/spec/markdown").then((r) => r.data),
  eventTypes: () => api.get("/webhooks/event-types").then((r) => r.data),
  list: () => api.get("/webhooks").then((r) => r.data),
  get: (id) => api.get(`/webhooks/${id}`).then((r) => r.data),
  create: (body) => api.post("/webhooks", body).then((r) => r.data),
  update: (id, body) => api.patch(`/webhooks/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/webhooks/${id}`).then((r) => r.data),
  rotate: (id) => api.post(`/webhooks/${id}/rotate-secret`).then((r) => r.data),
  commitRotation: (id) => api.post(`/webhooks/${id}/commit-rotation`).then((r) => r.data),
  test: (id) => api.post(`/webhooks/${id}/test`).then((r) => r.data),
  deliveries: (id, params = {}) =>
    api.get(`/webhooks/${id}/deliveries`, { params }).then((r) => r.data),
  deliveriesByTx: (tx_id) =>
    api.get(`/webhooks/deliveries-by-tx/${tx_id}`).then((r) => r.data),
  delivery: (delivery_id) =>
    api.get(`/webhooks/deliveries/${delivery_id}`).then((r) => r.data),
  retryDelivery: (delivery_id) =>
    api.post(`/webhooks/deliveries/${delivery_id}/retry`).then((r) => r.data),
  // Super-admin
  adminMetrics: () => api.get("/admin/webhooks/metrics").then((r) => r.data),
  adminListAll: () => api.get("/admin/webhooks/subscriptions").then((r) => r.data),
  adminPrune: () => api.post("/admin/webhooks/prune").then((r) => r.data),
  adminOverrideStatus: (id, status) =>
    api.post(`/admin/webhooks/${id}/override-status`, { status }).then((r) => r.data),
};

// ------- Odoo integration (Phase 9) -------
export const odooApi = {
  spec: () => pub.get("/integrations/odoo/spec").then((r) => r.data),
  specMarkdownUrl: () => `${BACKEND_URL}/api/integrations/odoo/spec/markdown`,
  status: () => api.get("/integrations/odoo/status").then((r) => r.data),
  getConnection: () => api.get("/integrations/odoo/connection").then((r) => r.data),
  saveConnection: (body) =>
    api.post("/integrations/odoo/connection", body).then((r) => r.data),
  testConnection: (body) =>
    api.post("/integrations/odoo/test-connection", body).then((r) => r.data),
  deleteConnection: () =>
    api.delete("/integrations/odoo/connection").then((r) => r.data),
  enable: () => api.post("/integrations/odoo/enable").then((r) => r.data),
  disable: () => api.post("/integrations/odoo/disable").then((r) => r.data),
  pullInvoices: () =>
    api.post("/integrations/odoo/pull-invoices").then((r) => r.data),
};

// ------- Business API keys (Phase 10.0) -------
export const apiKeysApi = {
  scopes: () => api.get("/business/api-keys/scopes").then((r) => r.data),
  list: () => api.get("/business/api-keys").then((r) => r.data),
  mint: (body) => api.post("/business/api-keys", body).then((r) => r.data),
  revoke: (id) => api.delete(`/business/api-keys/${id}`).then((r) => r.data),
  rotate: (id, grace_minutes) =>
    api
      .post(
        `/business/api-keys/${id}/rotate`,
        grace_minutes ? { grace_minutes } : {}
      )
      .then((r) => r.data),
};

// Helper for parsing FastAPI error detail (string OR validation array OR structured object)
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join("; ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return JSON.stringify(detail);
}
